import React from "react"
import Parse from "parse"
import ModeNightOutlinedIcon from "@mui/icons-material/ModeNightOutlined"
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined"
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined"
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"

import { COLORS } from "../utils"
import { convertTimestampToBlockIndex, getIngredientsFromPSESnapshot } from "./productionStepExecution"
import { ProductionStepExecution } from "../parseManager/productionStepExecutions/parseProductionStepExecutionManager"
import { PSE_STATUS } from "../actions/ProductionSteps/ProductionStepExecutions"
import moment from "moment"

/**
 * Format PSEs for display with all necessary data
 * Transforms raw PSE data into display-ready format
 * @param {Array} productionStepsExecutions - Raw PSE array from Redux
 * @param {Object} options - Formatting options
 * @param {boolean} options.withIngredients - Whether to fetch ingredients (async, for ProdPlanner). Default: true
 * @param {boolean} options.withPlannerData - Whether to compute planner-specific data (start/end/machine indices). Default: true
 * @returns {Promise<Array>} - Formatted PSE array with all data ready to display
 */
export const formatPSEsToTiles = async (productionStepsExecutions, options = {}) => {
	const { withIngredients = true, withPlannerData = true, productionDay } = options

	if (!productionStepsExecutions || !Array.isArray(productionStepsExecutions)) return []

	// Fetch all outputPSE statuses in one query and inject them into the snapshots
	const allOutputPSEIds = productionStepsExecutions.flatMap(pse =>
		((pse.get ? pse.get("productionStepSnapshot") : pse.productionStepSnapshot)?.stepComponents || [])
			.map(sc => sc?.sectionOutput?.outputPSE?.objectId).filter(Boolean)
	)

	if (allOutputPSEIds.length > 0) {
		const outputPSEs = await new Parse.Query(ProductionStepExecution).containedIn("objectId", allOutputPSEIds).select(["status"]).find()
		const statusMap = Object.fromEntries(outputPSEs.map(p => [p.id, p.get("status")]))

		for (const pse of productionStepsExecutions) {
			const snapshot = pse.get ? pse.get("productionStepSnapshot") : pse.productionStepSnapshot
			for (const sc of snapshot?.stepComponents || []) {
				const outputPSE = sc?.sectionOutput?.outputPSE
				if (outputPSE?.objectId && outputPSE.objectId in statusMap) {
					outputPSE.status = statusMap[outputPSE.objectId]
				}
			}
		}
	}

	const formattedPSEs = []
	for (const pse of productionStepsExecutions) {
		const productionStepSnapshot = pse.get ? pse.get("productionStepSnapshot") : pse.productionStepSnapshot
		const kitchenArea = productionStepSnapshot ? productionStepSnapshot.kitchenArea : {}
		const kitchenAreaId = kitchenArea.objectId || kitchenArea.id || null
		const machinesBatch = (pse.get ? pse.get("machinesBatch") : pse.machinesBatch) || []

		if (withPlannerData && machinesBatch.length > 1) {
			// ProdPlanner: duplicate PSEs for multiple machines
			for (let i = 0; i < machinesBatch.length; i++) {
				const batch = machinesBatch[i]
				const isOriginal = i === 0
				const startTime = isOriginal ? pse.theoreticalStartTime : pse.theoreticalStartTime + batch.startTimeOffset
				const productionDate = pse.get ? pse.get("productionDate") : pse.productionDate
				const theoreticalEndTime = pse.get ? pse.get("theoreticalEndTime") : pse.theoreticalEndTime
				const startIndex = kitchenAreaId ? convertTimestampToBlockIndex(startTime, productionDate) : null
				const endIndex = kitchenAreaId ? convertTimestampToBlockIndex(theoreticalEndTime, productionDate) : null
				const machineIndex = (batch.machine ? (batch.machine.objectId || batch.machine.id || null) : null)

				formattedPSEs.push(await buildTile(pse, {
					objectId: isOriginal ? (pse.objectId || pse.id || null) : (pse.objectId || pse.id || null) + "_dup-" + i,
					originalId: isOriginal ? undefined : (pse.objectId || pse.id || null),
					startIndex,
					endIndex,
					machineIndex,
					withIngredients,
					withPlannerData,
					productionDay
				}))
			}
		} else {
			// Single machine or no machine, or not for planner
			let startIndex = null, endIndex = null, machineIndex = null
			const firstMachineBatch = machinesBatch[0] || {}
			if (withPlannerData && pse.theoreticalStartTime && pse.theoreticalEndTime && firstMachineBatch.machine && kitchenAreaId) {
				const productionDate = pse.get ? pse.get("productionDate") : pse.productionDate
				const theoreticalStartTime = pse.get ? pse.get("theoreticalStartTime") : pse.theoreticalStartTime
				const theoreticalEndTime = pse.get ? pse.get("theoreticalEndTime") : pse.theoreticalEndTime
				startIndex = convertTimestampToBlockIndex(theoreticalStartTime, productionDate)
				endIndex = convertTimestampToBlockIndex(theoreticalEndTime, productionDate)
				machineIndex = firstMachineBatch.machine.objectId || firstMachineBatch.machine.id || null
			}
			formattedPSEs.push(await buildTile(pse, {
				objectId: pse.objectId || pse.id || null,
				originalId: undefined,
				startIndex,
				endIndex,
				machineIndex,
				withIngredients,
				withPlannerData,
				productionDay
			}))
		}
	}

	return formattedPSEs
}

/**
 * Build a formatted tile object from a PSE and options.
 * Optionally fetches ingredients asynchronously.
 * Used by formatPSEsToTiles to avoid redundancy.
 */
export const buildTile = async (pse, opts) => {
	const getOrProperties = (field) => pse.get ? pse.get(field) : pse[field]
	const { withIngredients = true, withPlannerData = true, productionDay } = opts

	// Only fetch ingredients if needed (expensive async operation)
	const productionStepSnapshot = getOrProperties("productionStepSnapshot")
	const productionDate = getOrProperties("productionDate")
	const ingredients = withIngredients
		? (await getIngredientsFromPSESnapshot(productionStepSnapshot, pse.objectId || pse.id, productionDate) || [])
		: []

	// Compute visual flags (same logic used by getTileVisuals)
	//   const description = pse.productionStepSnapshot?.description || ""
	const description = productionStepSnapshot ? productionStepSnapshot.description : ""
	const name = productionStepSnapshot ? productionStepSnapshot.name : ""
	const transformation = productionStepSnapshot ? productionStepSnapshot.transformation : undefined
	const ulteriorStep = pse.get ? pse.get("ulteriorStep") : pse.ulteriorStep
	const ulteriorStepProductionStepSnapshot = ulteriorStep ? ulteriorStep.productionStepSnapshot : {}
	const ulteriorTransformation = ulteriorStepProductionStepSnapshot ? ulteriorStepProductionStepSnapshot.transformation : undefined
	const mergedProductionStepExecutions = getOrProperties("mergedProductionStepExecutions")
	const productionDayTimestamp = productionDay ? moment.utc(productionDay).startOf("day").valueOf() : null

	const tile = {
		// Core PSE data (always included)
		objectId: opts.objectId,
		recipeName: getOrProperties("recipeName") || "",
		uniqueCode: getOrProperties("uniqueCode") || "-",
		sectionName: getOrProperties("sectionName") || "-",
		PSEName: name || "-",
		status: getOrProperties("status"),
		theoreticalStartTime: getOrProperties("theoreticalStartTime"),
		theoreticalEndTime: getOrProperties("theoreticalEndTime"),
		startTime: getOrProperties("startTime"),
		endTime: getOrProperties("endTime"),
		theoreticalGrossWeight: getOrProperties("theoreticalGrossWeight") || 0,
		theoreticalNetWeight: getOrProperties("theoreticalNetWeight") || 0,
		productionDate: getOrProperties("productionDate") || "-",
		productionStepSnapshot: productionStepSnapshot, // useful for searching/filtering
		machinesBatch: getOrProperties("machinesBatch") || [], // useful for searching/filtering

		// Visual flags (computed from raw PSE data)
		isStepMerged: getOrProperties("isStepMerged") || false,
		isReprod: !!getOrProperties("original"),
		isEmployeesMeal: (getOrProperties("productionItems") || []).some(item => item.brand === "EMPLOYEES_MEALS") || false,
		isNight: productionDayTimestamp && getOrProperties("stepProductionDate") && (getOrProperties("stepProductionDate") < productionDayTimestamp),
		isMixingHot: transformation === "COOKING" && ulteriorTransformation === "MIXED",
		isSync: /synchro/i.test(description) || /synchro/i.test(name),

		// Merged PSE data
		isStepMergedDict: (mergedProductionStepExecutions || []).length > 0
			? mergedProductionStepExecutions.reduce((dict, item) => {
				dict[item.uniqueCode] = {
					sectionName: item.sectionName,
					recipeName: item.recipeName
				}
				return dict
			}, {})
			: null,
		mergedProductionStepExecutions: mergedProductionStepExecutions || [],
		isInsufficientWeight: (pse.get ? pse.get("subStatus") : pse.subStatus) === "INSUFFICIENT_WEIGHT" || false,

		// Prior steps (for locked status display)
		remainingPriorSteps: [
			// Direct PSE prior steps that are not yet done
			...((pse.get ? pse.get("priorSteps") : pse.priorSteps) || [])
				.filter(prior => prior.step && prior.step.status !== "DONE")
				.map(prior => ({
					name: prior.step && prior.step.productionStepSnapshot && prior.step.productionStepSnapshot.name || "",
					kitchenArea: prior.step && prior.step.productionStepSnapshot && prior.step.productionStepSnapshot.kitchenArea && prior.step.productionStepSnapshot.kitchenArea.name || "",
				})),
			// Section output dependencies (ingredients coming from another section's output)
			...(productionStepSnapshot?.stepComponents || [])
				.filter(sc => sc?.sectionOutput?.outputPSE && sc.sectionOutput.outputPSE.status !== PSE_STATUS.done)
				.map(sc => ({
					name: [sc.sectionOutput.sectionName, sc.sectionOutput.name].filter(Boolean).join(" - ") || "",
					kitchenArea: sc.sectionOutput.kitchenArea && sc.sectionOutput.kitchenArea.name || "",
				})),
		],

		// Raw snapshot for additional data access
		ulteriorStep,
		machineNames: ((pse.get ? pse.get("machinesBatch") : pse.machinesBatch) || []).map(batch => batch.machineName),
	}
	const productionItems = (pse.get ? pse.get("productionItems") : pse.productionItems) || []
	const firstProductionItem = productionItems[0]
	const firstProductionItemDate = firstProductionItem ? (firstProductionItem.get ? firstProductionItem.get("packagingDate") : firstProductionItem.packagingDate) : null
	// Add planner-specific data only if needed
	if (withPlannerData) {
		tile.originalId = opts.originalId
		tile.start = opts.startIndex
		tile.end = opts.endIndex
		tile.machine = opts.machineIndex
		tile.duration = opts.endIndex !== null && opts.startIndex !== null ? opts.endIndex - opts.startIndex : null
		tile.packagingDate = firstProductionItemDate || "-"
		tile.description = description || "Aucune instruction disponible."
		tile.ingredients = ingredients
	}

	return tile
}

/**
 * PSE Tile color and icon configuration based on PSE properties
 * Used by both ProdPlannerTile and ProductionStepExecutionsTile
 */
export const PSE_TILE_VISUALS = {
	merged: {
		color: COLORS.RED_100,
		icon: <img src={"/icons/dynamic-feed.svg"} alt="dynamic-feed" style={{ height: 24, width: 24, padding: "2px" }} />,
		border: "none",
		label: "Mutualisées",
		chipBorder: "1px solid " + COLORS.RED_200,
		chipFont: COLORS.RED_800,
		chipBackground: COLORS.RED_50,
	},
	insufficientWeight: {
		label: "Quantité insuffisante",
		chipBorder: "1px solid " + COLORS.RED_200,
		chipFont: COLORS.RED_800,
		chipBackground: COLORS.RED_50,
	},
	reprod: {
		color: COLORS.GREEN_300,
		icon: <RepeatOutlinedIcon style={{ height: 24, width: 24, padding: "2px" }} />,
		border: "none",
		label: "Reprod",
		chipBorder: "1px solid " + COLORS.GREEN_300,
		chipFont: COLORS.GREEN_800,
		chipBackground: COLORS.GREEN_50,
	},
	mixingHot: {
		color: COLORS.PURPLE_100,
		icon: <img src={"/icons/pot-mix.svg"} alt="pot-mix" style={{ height: 24, width: 24, padding: "2px" }} />,
		border: "none",
		label: "À chaud",
		chipBorder: "1px solid " + COLORS.PURPLE_100,
		chipFont: COLORS.PURPLE_800,
		chipBackground: COLORS.PURPLE_50,
	},
	night: {
		color: COLORS.DARK_BLUE_100,
		icon: <ModeNightOutlinedIcon style={{ height: 24, width: 24, padding: "2px" }} />,
		border: "none",
		label: "Veille",
		chipBorder: "1px solid " + COLORS.DARK_BLUE_200,
		chipFont: COLORS.SECONDARY_DLC_BLUE,
		chipBackground: COLORS.DARK_BLUE_50,
	},
	employeesMeal: {
		color: COLORS.ORANGE_150,
		icon: <PeopleAltOutlinedIcon style={{ height: 24, width: 24, padding: "2px" }} />,
		border: "none",
		label: "Employés",
		chipBorder: "1px solid " + COLORS.ORANGE_150,
		chipFont: COLORS.ORANGE_800,
		chipBackground: COLORS.ORANGE_50,
	},
	sync: {
		color: COLORS.MINT_200,
		icon: <HourglassBottomIcon style={{ height: 24, width: 24, padding: "2px" }} />,
		border: "none",
		label: "Synchronisation",
		chipBorder: "1px solid " + COLORS.MINT_300,
		chipFont: COLORS.MINT_500,
		chipBackground: COLORS.MINT_50,
	},
	locked: {
		color: COLORS.BACKGROUND_LIGHT_GREY,
		icon: <LockOutlinedIcon style={{ height: 24, width: 24, padding: "2px" }} />,
		border: "none"
	},
	unlocked: {
		color: COLORS.WHITE,
		icon: null,
		border: "1px solid " + COLORS.BLACK_200
	},
}


/**
 * Get tile visual properties (background color and icon/border) based on formatted PSE/tile properties
 * Expects tiles formatted by formatPSEsToTiles which have pre-computed visual flags (isReprod, isNight, etc.)
 * 
 * @param {Object} tile - The formatted PSE/tile object with visual flags
 * @param {boolean} isForProdPlanner - If true, returns background colors/icons for all types. If false, returns only locked/unlocked backgrounds
 * @returns {{ color: string, icon: React.ReactNode | null, border: string }}
 */
export const getTileVisuals = (tile, isForProdPlanner = false) => {
	if (!tile) return PSE_TILE_VISUALS.unlocked

	if (isForProdPlanner) {
		// ProdPlanner: return background color and icon for all types except unlocked
		if (tile.status === "LOCKED") return PSE_TILE_VISUALS.locked
		if (tile.isStepMerged) return PSE_TILE_VISUALS.merged
		if (tile.isReprod) return PSE_TILE_VISUALS.reprod
		if (tile.isMixingHot) return PSE_TILE_VISUALS.mixingHot
		if (tile.isNight) return PSE_TILE_VISUALS.night
		if (tile.isEmployeesMeal) return PSE_TILE_VISUALS.employeesMeal
		if (tile.isSync) return PSE_TILE_VISUALS.sync

		return PSE_TILE_VISUALS.unlocked
	} else {
		// Non-ProdPlanner:
		if (tile.status === "LOCKED") return PSE_TILE_VISUALS.locked
		if (tile.isStepMerged) return PSE_TILE_VISUALS.merged
		if (tile.isNight) return PSE_TILE_VISUALS.night
		if (tile.isSync) return PSE_TILE_VISUALS.sync
		if (tile.isReprod) return PSE_TILE_VISUALS.reprod

		return PSE_TILE_VISUALS.unlocked
	}
}

/**
 * Get all active chip visuals (color, label, icon) for a tile
 * Returns an array of visual objects for chip rendering (excludes locked/unlocked)
 * 
 * @param {Object} tile - The formatted PSE/tile object with visual flags
 * @returns {Array<{color: string, label: string, icon: React.ReactNode}>}
 */
export const getTileChips = (tile) => {
	if (!tile) return []

	const chips = []

	if (tile.isStepMerged) chips.push(PSE_TILE_VISUALS.merged)
	if (tile.isReprod) chips.push(PSE_TILE_VISUALS.reprod)
	// if (tile.isMixingHot) chips.push(PSE_TILE_VISUALS.mixingHot)
	if (tile.isNight) chips.push(PSE_TILE_VISUALS.night)
	// if (tile.isEmployeesMeal) chips.push(PSE_TILE_VISUALS.employeesMeal)
	if (tile.isSync) chips.push(PSE_TILE_VISUALS.sync)
	if (tile.isInsufficientWeight) chips.push(PSE_TILE_VISUALS.insufficientWeight)

	return chips
}

export const PE_CARDS_THEME = {
	gray: {
		main: "transparent",
		light: "#F8F8F8",
		dark: "#F8F8F8",
		default: "#F0F0F0"
	},
	orange: {
		main: "#FDF0D2",
		light: "#FFF9E8",
		dark: "#E98232",
		default: "#FDF0D2"
	},
	blue: {
		main: "#BBDEFB",
		light: "#F0F9FF",
		dark: "#1663C0",
		default: "#E3F2FD"
	},
	violet: {
		main: "#F1E7FF",
		light: "#FAF5FF",
		dark: "#7900D9",
		default: "#F1E7FF"
	}
}

export const parsePESToCardColors = (lunchbag, nationalSend) => {
	if (lunchbag && nationalSend) {
		return PE_CARDS_THEME.violet
	}

	if (lunchbag) {
		return PE_CARDS_THEME.blue
	}

	if (nationalSend) {
		return PE_CARDS_THEME.orange
	}

	return PE_CARDS_THEME.gray
}
